import { Dialog, DialogActions, DialogContent } from '@material-ui/core';
import * as React from 'react';
import { connect } from 'react-redux';
import { setUserServiceCenter } from '../../actions/userdata-actions';
import { FlatButton } from '../../components/button/FlatButton';
import { DialogHeader } from '../../components/dialog/DialogHeader';
import { AppUser } from '../../models/utilities/app-user';
import { ServiceCenter } from '../../models/utilities/service-center';
import { getServiceCenterData } from '../../selectors/utility-selectors';
import { IApplicationState } from '../../store/IApplicationState';
import './ServiceCenterPicker.scss';
import { showLoading } from '../../actions/utility-actions';
// #region Component State

const initialState = {
    open                : false,
    newServiceCenter    : new ServiceCenter()
};

type State = (typeof initialState);

// #endregion

// #region Component Props

interface IProps {
    changeServiceCenter : any;
    activeServiceCenter : ServiceCenter;
    serviceCenters      : ServiceCenter[];
    userServiceCenters  : string[];
    showLoading         : any;
}

// #endregion

class ServiceCenterPickerComponent extends React.Component<IProps, State> {

    public state : State = initialState;

    public componentDidMount() {
        this.setState({
            newServiceCenter: this.props.activeServiceCenter
        });       
    }

    public UNSAFE_componentWillReceiveProps(newProps: IProps) {
        if (newProps.activeServiceCenter) {
            this.setState({
                newServiceCenter: newProps.activeServiceCenter
            });
        }
    }

    /**
     * The service centers the user can choose from: all of them for someone who can work in ALL, otherwise the ones they were given
     */
    public availableServiceCenters = (): ServiceCenter[] => {
        const mine = this.props.userServiceCenters || [];
        return mine.some(x => x.toUpperCase() === 'ALL')
            ? this.props.serviceCenters
            : this.props.serviceCenters.filter(sc => AppUser.canWorkIn(mine, sc.name));
    }

    public handleClickOpen = () => {
        // there is something to choose when there is more than one service center to work in
        if (this.availableServiceCenters().length > 1) {
            this.setState({
                open: true,
                newServiceCenter: this.props.activeServiceCenter
            });
        }
    }

    public handleClose = () => {
        this.setState({ 
            open: false             
        });
    }

    public handleChange = (e: any) => {
        const selected = this.props.serviceCenters.find((sc : ServiceCenter) => {
            const tmp = Number.parseInt(e.target.value, 10);
            return sc.id === tmp;
        });
        
        if (selected) {
            this.setState({
                newServiceCenter: selected
            });
        }
    }

    public setNewServiceCenter = () => {
        this.props.showLoading();
        this.props.changeServiceCenter(this.state.newServiceCenter);
        this.setState({
            open: false
        });
        
        // reload the app to reset the role
        // heavy handed for now, but it works 
        location.reload(true);
    }

    public render() {

        const { activeServiceCenter } = this.props;
        const serviceCenters = this.availableServiceCenters();

        if (!activeServiceCenter) {
            return null;
        }

        // There is something to choose when there is more than one service center to work in, and the card says so
        const canChange = serviceCenters.length > 1;

        return (
            <React.Fragment>

                <button
                    type="button"
                    className={ canChange ? 'service-center-trigger' : 'service-center-trigger is-fixed' }
                    title={ canChange ? 'Change service center' : 'Service center' }
                    aria-label={ `Service center ${ activeServiceCenter.description }${ canChange ? ', change' : '' }` }
                    onClick={this.handleClickOpen}>
                    <span className="service-center-icon"><i className="la la-map-marker" /></span>
                    <span className="service-center-text">
                        <span className="service-center-name">{ activeServiceCenter.description }</span>
                        <span className="service-center-code">Service center · { activeServiceCenter.name }</span>
                    </span>
                    { canChange ? <i className="la la-exchange service-center-change" /> : null }
                </button>

                <Dialog
                    open={this.state.open}
                    onClose={this.handleClose}
                    maxWidth="md"
                    aria-labelledby="form-dialog-title">

                    <DialogHeader>
                        <h1>Change Service Center</h1>
                    </DialogHeader>

                    <DialogContent className="dialog-body-content">
                        <div className="row">
                            <div className="col">
                                <p className="pt-3 pb-2">
                                    Your current service center is: <span>{ activeServiceCenter.name }</span>
                                </p>
                            </div>
                        </div>
                        <div className="row">
                            <div className="col">
                                <div className="form-group">
                                    <label>New Facility</label>
                                    <select 
                                        className="form-control" 
                                        value={this.state.newServiceCenter.id} 
                                        onChange={this.handleChange}>
                                        {
                                            serviceCenters.map((sc: ServiceCenter) => (
                                            <option key={sc.id} value={sc.id}>[{sc.name}] : {sc.description}</option>
                                            ))
                                        }
                                    </select>
                                </div>           
                            </div>             
                        </div>          
                        <div className="row">
                            <div className="col">
                                    <p className="disclaimer-text">
                                        By clicking 'Save', your session will refresh and 
                                        any pending changes you have will be lost.
                                    </p>
                            </div>
                        </div>
                    </DialogContent>
                    <DialogActions>
                        <FlatButton
                            id="scConfirmCancelBtn"
                            text="Cancel"
                            classes="negative"
                            onBtnClick={this.handleClose} />
                        <FlatButton
                            id="scConfirmSaveBtn"
                            text="Save"
                            classes="positive"
                            onBtnClick={this.setNewServiceCenter} />
                    </DialogActions>
                </Dialog>
            </React.Fragment>
        );
    }

}

const mapStateToProps = (state: IApplicationState) => {
    return {
        serviceCenters: getServiceCenterData(state),
        activeServiceCenter: state.userdata.activeServiceCenter,
        userServiceCenters: state.userdata.user.serviceCenters
    };
};

const mapDispatchToProps = (dispatch: any) => {
    return {
        changeServiceCenter: (itm: ServiceCenter) => dispatch(setUserServiceCenter(itm)),
        showLoading: () => dispatch(showLoading())
    };
};

const ServiceCenterPicker = connect(
    mapStateToProps,
    mapDispatchToProps
  )(ServiceCenterPickerComponent);
  
export { ServiceCenterPicker };
